import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import {
  People as PeopleIcon,
  AssignmentInd as CustomersIcon,
  Receipt as SubscriptionsIcon,
  Notifications as NotificationsIcon,
  EmojiEvents as AchievementsIcon,
  BugReport as TestRunsIcon,
  Apps as AppVersionIcon,
  Star as FeaturesIcon,
  Work as JobsIcon,
  Message as HelpCenterIcon,
  Dashboard as DashboardIcon,
  TrendingDown as FunnelIcon,
  Code as PromptManagementIcon,
} from '@mui/icons-material';

const SidebarContainer = styled.aside`
  width: 260px;
  height: 100vh;
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-right: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  z-index: 1000;
  transition: all 0.3s ease;

  ${props => props.theme?.isDark && `
    box-shadow: 0 0 20px ${getTheme(true).colors.glow};
  `}
`;

const LogoContainer = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
  border-bottom: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
`;

const LogoText = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  margin: 0;
  ${props => props.theme?.isDark && 'text-shadow: 0 0 10px rgba(0, 255, 0, 0.5);'}
`;

const NavList = styled.nav`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  text-decoration: none;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
    color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(0, 255, 0, 0.2);
    `}
  }

  ${props => props.active && `
    background-color: ${getTheme(props.theme?.isDark).colors.primary}20;
    color: ${getTheme(props.theme?.isDark).colors.primary};
    ${props.theme?.isDark && `
      box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
      border-left: 3px solid ${getTheme(true).colors.primary};
    `}
  `}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  font-size: 20px;
`;

const menuItems = [
  { path: '/admin', label: 'Dashboard', icon: DashboardIcon },
  { path: '/admin/customers', label: 'Customers', icon: CustomersIcon },
  { path: '/admin/customerFunnels', label: 'Customer Funnels', icon: FunnelIcon },
  { path: '/admin/users', label: 'Users', icon: PeopleIcon },
  { path: '/admin/subscriptions', label: 'Subscriptions', icon: SubscriptionsIcon },
  { path: '/admin/notifications', label: 'Notifications', icon: NotificationsIcon },
  { path: '/admin/achievements', label: 'Achievements', icon: AchievementsIcon },
  { path: '/admin/app-versions', label: 'App Versions', icon: AppVersionIcon },
  { path: '/admin/features', label: 'Features', icon: FeaturesIcon },
  { path: '/admin/jobs', label: 'Jobs', icon: JobsIcon },
  { path: '/admin/test-runs', label: 'Test Runs', icon: TestRunsIcon },
  { path: '/admin/prompt-management', label: 'Prompt Management', icon: PromptManagementIcon },
  { path: '/admin/help-center', label: 'Help Center', icon: HelpCenterIcon },
];

const Sidebar = () => {
  const location = useLocation();
  const { isDark } = useTheme();
  const theme = { isDark };

  return (
    <SidebarContainer theme={theme}>
      <LogoContainer theme={theme}>
        <Logo src="/fit-logo.png" alt="Fit Logo" />
        <LogoText theme={theme}>Admin Panel</LogoText>
      </LogoContainer>
      <NavList>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path !== '/admin' && location.pathname.startsWith(item.path));
          
          return (
            <NavItem
              key={item.path}
              to={item.path}
              active={isActive ? 1 : 0}
              theme={theme}
            >
              <IconWrapper>
                <Icon />
              </IconWrapper>
              <span>{item.label}</span>
            </NavItem>
          );
        })}
      </NavList>
    </SidebarContainer>
  );
};

export default Sidebar;

